import { h, FunctionComponent } from 'preact';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { TournamentDetails, PrizeTier, asGameEntry } from '../../../services/slot-tournaments';
import { GameIcon } from './casino/casino-contents';
import formatCurrency from '../../../services/currency';
import Autoplay from 'embla-carousel-autoplay';
import useEmblaCarousel from 'embla-carousel-react';
import { msg } from '@lingui/core/macro';

interface TournamentDetailsDisplayProps {
    tournament: TournamentDetails | null;
}

export const TournamentDetailsDisplay: FunctionComponent<TournamentDetailsDisplayProps> = ({ tournament }) => {
    if (!tournament) {
        // Render nothing or a placeholder if tournament data is not yet available or failed to load
        // The parent component handles the main loading/error states.
        return null;
    }
    const [emblaRef] = useEmblaCarousel({ loop: true, dragFree: true }, [Autoplay()])

    return (
        <div class="bg-harif-900 rounded-lg shadow-lg p-6 mb-8">
            <h2 class="text-2xl font-bold text-white mb-4">{tournament.title}</h2>
            <p class="text-gray-400 mb-4">{tournament.description}</p>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm mb-4">
                <div><strong class="text-gray-300"><Trans>Status</Trans>:</strong> <span class="capitalize text-white">{tournament.status}</span></div>
                <div><strong class="text-gray-300"><Trans>Starts</Trans>:</strong> <span class="text-white">{new Date(tournament.start_date).toLocaleString()}</span></div>
                <div><strong class="text-gray-300"><Trans>Ends</Trans>:</strong> <span class="text-white">{new Date(tournament.end_date).toLocaleString()}</span></div>
                <div><strong class="text-gray-300"><Trans>Winning Criteria</Trans>:</strong> <span class="text-white">{tournament.winning_criteria}</span></div>
                {tournament.minimal_bet && (
                    <div><strong class="text-gray-300"><Trans>Min Bet</Trans>:</strong> <span class="text-white">{tournament.minimal_bet} {tournament.minimal_bet_currency}</span></div>
                )}
                {tournament.minimal_rounds && (
                    <div><strong class="text-gray-300"><Trans>Min Rounds</Trans>:</strong> <span class="text-white">{tournament.minimal_rounds}</span></div>
                )}
            </div>

            <div class="mb-4">
                <h4 class="text-lg font-semibold text-white mb-2"><Trans>Eligible Slots</Trans>:</h4>
                {tournament.slots && tournament.slots.length > 0 ? (
                    <div class="embla h-48" ref={emblaRef}>
                        <div class="embla__container gap-2 h-full">
                            {tournament.slots.map(slot => asGameEntry(slot)).map(row => <GameIcon game={row} />)}
                        </div>
                    </div>
                ) : (
                    <p class="text-gray-500 text-sm"><Trans>Details not available.</Trans></p>
                )}
            </div>

            <div>
                <h4 class="text-lg font-semibold text-white mb-2"><Trans>Prize Structure</Trans>:</h4>
                {tournament.prize_structure && tournament.prize_structure.length > 0 ? (
                    <ul class="list-disc list-inside text-gray-400 text-sm">
                        {tournament.prize_structure.map((tier: PrizeTier, index: number) => (
                            <li key={index}>
                                {window.Translation(msg`Rank ${tier.rank}`)} {tier.rank_to ? ` - ${tier.rank_to}` : ''} : {formatCurrency(tier.amount)} {tier.currency}
                            </li>
                        ))}
                    </ul>
                ) : (
                    <p class="text-gray-500 text-sm"><Trans>Details not available.</Trans></p>
                )}
            </div>
        </div>
    );
};